import React, { useState, useEffect, useRef } from 'react';
import { notification, DatePicker, theme, Button, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useFilters from 'utils/hooks/useFilters';
import CustomTable, {
  EditableColumnType,
} from 'components/CustomTable/CustomTable';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import useICTSystems from 'utils/hooks/fetchHooks/useICTSystems';
import { functionOp } from 'store/ducks/DataEntry/Functions';
import { requiredFieldsForICTSystems } from 'constants/requiredFields';
import CustomSelect from 'components/CustomSelect';
import { globalOp } from 'store/ducks/Global';
import * as Styled from '../../DataEntry/DataEntry.styled';
import { article8Op } from 'store/ducks/DataManagement/Article8AndICTSystem';
import SaveButton from 'components/SaveButton/SaveButton';
import { useTranslation } from 'react-i18next';
import { DataTypeCustomTable } from 'store/ducks/Global/types';
import { deleteRecord } from 'utils/hooks/deleteRecord';

interface ICTSystem extends DataTypeCustomTable {
  key: number;
  id: string;
  label: string;
  value: string;
  description: string;
  start_date: string;
  end_date: string;
  ict_system_type_id: number | string;
  ict_system_criticality_id: number | string;
  ict_system_category_id: string;
  access_restricted: boolean;
  ict_system_site: string;
  isNew?: boolean;
  isEdited?: boolean;
}

const ICTSystem = () => {
  const { query } = useFilters();
  dayjs.extend(customParseFormat);
  const { ictSystems } = useICTSystems(
    query.entityId ?? '',
    query.entity ?? '',
  );
  const { t } = useTranslation();
  const [data, setData] = useState<ICTSystem[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [initialData, setInitialData] = useState<ICTSystem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [changedData, setChangedData] = useState<Record<string, any>[]>([]);
  const { useToken } = theme;
  const { token } = useToken();
  const { confirm } = Modal;
  const [ictSystemType, setICTSystemType] = useState<
    {
      description: string;
      id: number;
      end_date: string;
      name: string;
      start_date: string;
    }[]
  >([]);

  const [ictSystemCriticality, setICTSystemCriticality] = useState<
    {
      id: number;
      name: string;
    }[]
  >([]);

  const [ictSystemCategory, setICTSystemCategory] = useState<
    {
      description: string;
      id: number;
      end_date: string;
      name: string;
      start_date: string;
    }[]
  >([]);

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const fetchICTSystemTypes = async () => {
    try {
      const response = await globalOp.fetchICTSystemTypes();
      if (response?.data.data) {
        const formattedOptions = response.data.data.map(
          (type: {
            name: string;
            id: number;
            description: string;
            end_date: string;
            start_date: string;
          }) => ({
            description: type.description,
            id: type.id,
            end_date: type.end_date,
            name: type.name,
            start_date: type.start_date,
          }),
        );
        setICTSystemType(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchICTSystemCriticality = async () => {
    try {
      const response = await article8Op.getICTSystemCriticality();
      const data = response?.data?.ict_system_criticalities;
      if (response?.data.ict_system_criticalities) {
        const formattedOptions = response.data.ict_system_criticalities.map(
          (type: { name: string; id: number }) => ({
            id: type.id,
            name: type.name,
          }),
        );
        setICTSystemCriticality(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchICTSystemCategories = async () => {
    try {
      const response = await globalOp.fetchICTSystemCategories();
      if (response?.data.data) {
        const formattedOptions = response.data.data.map(
          (type: {
            name: string;
            id: number;
            description: string;
            end_date: string;
            start_date: string;
          }) => ({
            description: type.description,
            id: type.id,
            end_date: type.end_date,
            name: type.name,
            start_date: type.start_date,
          }),
        );
        setICTSystemCategory(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchICTSystems = async () => {
    const formattedData: ICTSystem[] = ictSystems.map((system) => ({
      id: String(system.value),
      key: Number(system.value),
      value: String(system.value),
      label: system.label,
      description: system.description,
      start_date: system.start_date,
      end_date: system.end_date,
      access_restricted: system.access_restricted,
      ict_system_type_id: system.ict_system_type_id,
      ict_system_criticality_id: system.ict_system_criticality_id,
      ict_system_category_id: system.ict_system_category_id,
      ict_system_site: system.ict_system_site,
      entity_id: system.entity_id,
      ict_system_category_name: system.ict_system_category_name,
      ict_system_criticality: system.ict_system_criticality,
      ict_system_type_name: system.ict_system_type_name,
    }));

    setData(formattedData);
    setInitialData(formattedData);
  };

  const handleSave = (row: Partial<ICTSystem> & { key: number }) => {
    const isRowInDataSource = data.some((item) => item.key === row.key);

    if (isRowInDataSource) {
      const updatedDataSource = data.map((item) =>
        item.key === row.key
          ? { ...item, ...row, isEdited: !item.isNew }
          : item,
      );
      setData(updatedDataSource as ICTSystem[]);
    }
  };

  useEffect(() => {
    if (query) {
      fetchICTSystems();
      fetchICTSystemTypes();
      fetchICTSystemCriticality();
      fetchICTSystemCategories();
    }
  }, [query, ictSystems]);

  const addNewRow = () => {
    const uid = generateUniqueId();

    const newRowTemplate: ICTSystem = {
      id: String(uid),
      key: uid,
      value: String(uid),
      label: '',
      description: '',
      start_date: '',
      end_date: '9999-01-01',
      access_restricted: false,
      ict_system_category_id: '',
      ict_system_type_id: '',
      ict_system_criticality_id: '',
      ict_system_site: '',
      isNew: true,
      isEdited: true,
    };

    setData((prevData) => [...prevData, newRowTemplate]);
    setChangedData((prevChangedData) => [...prevChangedData, newRowTemplate]);
  };

  const handleSubmitSystem = async () => {
    const changedData = data.filter((item) => item.isNew || item.isEdited);
    if (JSON.stringify(data) === JSON.stringify(initialData)) {
      notification.info({
        message: 'Info',
        description: 'No new data has been changed. ',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const hasEmptyFields = changedData.some((item) =>
      (requiredFieldsForICTSystems as (keyof typeof item)[]).some(
        (field) => item[field] === '' || item[field] === null,
      ),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please make sure to enter a start date and ICT system fields before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const newICTPayload = {
      entity_name: query.entity,
      ICTSystems: changedData.map((system) => ({
        id: system.id,
        name: system.label,
        description: system.description,
        start_date: system.start_date,
        end_date: system.end_date || '9999-01-01',
        access_restricted: system.access_restricted,
        ict_system_category_id: Number(system.ict_system_category_id),
        ict_system_type_id: Number(system.ict_system_type_id),
        ict_system_site: system.ict_system_site,
        entity_id: query.entityId,
        ict_system_criticality_id: system.ict_system_criticality_id,
      })),
    };
    let response;

    try {
      response = await functionOp.insertFunctions(newICTPayload);

      if (response?.status === 200) {
        const resetData = data.map((item) => ({
          ...item,
          isNew: false,
          isEdited: false,
        }));
        setData(resetData);
        setInitialData(resetData);
        notification.success({
          message: 'Info',
          description:
            'The ICT system was successfully created. To attach it to the appropriate area, please navigate to the relevant section and select this ICT system from the list.',
          placement: 'topRight',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error creating the ICT system.',
        placement: 'topRight',
      });
    }
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = data.filter((item) => item.key !== key);
        setData(updatedDataSource);
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 10,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 10,
        });
      },
    });
  };
  const handleInactivate = (record: ICTSystem) => {
    if (!record.id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: Number(record.id),
      tableName: 'ict_systems',
      onSuccess: async () => {
        await fetchICTSystems();
      },
    });
  };
  // const handleInactivate = (record: ICTSystem) => {
  //   if (record.id === undefined) {
  //     notification.error({
  //       message: 'Info',
  //       description: 'The ICT System ID is missing.',
  //       placement: 'topRight',
  //       duration: 20,
  //     });
  //     return;
  //   }

  //   confirm({
  //     title: 'Confirm Inactivation',
  //     content:
  //       'Once deleted, this record cannot be restored and may affect dependent records. Would you like to continue?',
  //     okText: 'Yes',
  //     cancelText: 'No',
  //     onOk: async () => {
  //       try {
  //         const response = await globalOp.inactivateICTSystem([
  //           Number(record.id),
  //         ]);
  //         await fetchICTSystems();
  //       } catch (error) {
  //         console.log(error);
  //       }
  //     },
  //     onCancel: () => {},
  //   });
  // };

  const columns: EditableColumnType[] = [
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p> {t('ICT_SYSTEM.ICT_SYSTEM')}</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'label',
      editable: true,
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p> {t('ICT_SYSTEM.DESCRIPTION')}</p>
          </div>
        </Styled.TitleComponent>
      ),
      dataIndex: 'description',
      editable: true,
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICT_SYSTEM.ICT_SYSTEMS_VALID_FROM_TO')}</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'date_range',
      editable: false,
      render: (_: string, record: DataTypeCustomTable) => {
        const systemRecord = record as ICTSystem;

        return (
          <Styled.DateInputs>
            <DatePicker
              value={
                systemRecord.start_date ? dayjs(systemRecord.start_date) : null
              }
              format="YYYY-MM-DD"
              onClick={(e) => e.stopPropagation()}
              onChange={(date) => {
                const formattedDateString = date
                  ? dayjs(date).format('YYYY-MM-DD')
                  : '';

                const updatedRow: ICTSystem = {
                  ...systemRecord,
                  start_date: formattedDateString,
                };

                handleSave(updatedRow);
              }}
            />

            <DatePicker
              value={
                systemRecord.end_date ? dayjs(systemRecord.end_date) : null
              }
              format="YYYY-MM-DD"
              onClick={(e) => e.stopPropagation()}
              onChange={(date) => {
                const formattedDateString = date
                  ? dayjs(date).format('YYYY-MM-DD')
                  : '';

                const updatedRow: ICTSystem = {
                  ...systemRecord,
                  end_date: formattedDateString,
                };

                handleSave(updatedRow);
              }}
            />
          </Styled.DateInputs>
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICT_SYSTEM.ICT_SYSTEM_TYPE')}</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'ict_system_type_id',
      editable: false,
      render: (_, record) => (
        <CustomSelect
          options={ictSystemType.map((item) => ({
            label: item.name,
            value: String(item.id),
          }))}
          value={String(record.ict_system_type_id)}
          onChange={(value: string) =>
            handleSave({
              key: record.key,
              ict_system_type_id: value,
            })
          }
          filterOption={(input: string, option?: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICT_SYSTEM.ICT_SYSTEM_CATEGORY')}</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'ict_system_category_id',
      editable: false,
      render: (_, record) => (
        <CustomSelect
          options={ictSystemCategory.map((item) => ({
            label: item.name,
            value: String(item.id),
          }))}
          value={String(record.ict_system_category_id)}
          onChange={(value: string) =>
            handleSave({
              key: record.key,
              ict_system_category_id: value,
            })
          }
          filterOption={(input: string, option?: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: t('ICT_SYSTEM.ACCESS_RESTRICTED'),
      width: 150,
      dataIndex: 'access_restricted',
      key: 'access_restricted',
      render: (_, record) => (
        <CustomSelect
          options={[
            { label: t('GENERAL.YES'), value: 'true' },
            { label: t('GENERAL.NO'), value: 'false' },
          ]}
          value={String(record.access_restricted)}
          onChange={(value: string) =>
            handleSave({
              key: record.key,
              access_restricted: value === 'true',
            })
          }
          filterOption={(input: string, option?: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICT_SYSTEM.SITE')}</p>
          </div>
        </Styled.TitleComponent>
      ),
      dataIndex: 'ict_system_site',
      editable: true,
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICT_SYSTEM.ICT_SYSTEM_CRITICALITY')}</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'ict_system_criticality_id',
      editable: false,
      render: (_, record) => (
        <CustomSelect
          options={ictSystemCriticality.map((item) => ({
            label: item.name,
            value: String(item.id),
          }))}
          value={String(record.ict_system_criticality_id)}
          onChange={(value: string) =>
            handleSave({
              key: record.key,
              ict_system_criticality_id: value,
            })
          }
          filterOption={(input: string, option?: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: t('GENERAL.ACTIONS'),
      key: 'actions',
      render: (_: any, record) => (
        <>
          {record.isNew ? (
            <Button danger onClick={() => handleRemoveRow(record.key)}>
              {t('GENERAL.REMOVE')}
            </Button>
          ) : (
            <Button
              danger
              onClick={() => handleInactivate(record as ICTSystem)}
            >
              {t('GENERAL.SOFT_DELETE')}
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <Styled.HighlightedTable>
        <CustomTable
          columns={columns}
          dataSource={data}
          handleSave={handleSave}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            onChange: handlePageChange,
            showSizeChanger: true,
          }}
          locale={
            query.entity
              ? {
                  emptyText: (
                    <Button type="primary" onClick={() => addNewRow()}>
                      {t('GENERAL.ADD_FIRST_ROW')}
                    </Button>
                  ),
                }
              : undefined
          }
        />
      </Styled.HighlightedTable>
      <Styled.ButtonRow>
        {query.entity && data.length > 0 && (
          <Styled.AreaButton
            token={token}
            type="primary"
            onClick={() => addNewRow()}
          >
            Add new row
          </Styled.AreaButton>
        )}
        <SaveButton
          token={token}
          hasChanges={JSON.stringify(data) !== JSON.stringify(initialData)}
          onClick={handleSubmitSystem}
        />
      </Styled.ButtonRow>
    </div>
  );
};

export default ICTSystem;
